import {
  AutocompleteCategory,
  type VehicleAvailability,
  type Suggestion,
  type TrackingProperties,
  type Grounds,
  ShopResponse,
  TransmissionType,
} from "@b2bportal/ground-api";
import { TransmissionTypeEnum } from "@hopper-b2b/types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  CarsAvailabilitySelectors,
  CarsShopSelectors,
  type VehicleAvailabilityWithSupplier,
} from "../store";
import type { FilterState } from "../types";
import type {
  AppliedCarsFilterTrackingProperties,
  CarsEntryTrackingProperties,
  ViewedCarDetailsTrackingProperties,
  ViewedCarsListTrackingProperties,
} from "./types";

export interface CarLocationSuggestion extends Suggestion {
  category?: AutocompleteCategory;
  categoryLabel?: string;
  displayLabel?: string;
}

const formatCarEntryTrackingProperties = (
  pickUpDateTime: Date,
  pickUpLocation: CarLocationSuggestion,
  dropOffDateTime: Date,
  dropOffLocation: CarLocationSuggestion,
  driverAge: number
): CarsEntryTrackingProperties => {
  const cars_advance = dayjs(pickUpDateTime).diff(dayjs(), "day");
  const pick_up_date = dayjs(pickUpDateTime).format("YYYY-MM-DD");
  const drop_off_date = dayjs(dropOffDateTime).format("YYYY-MM-DD");
  const pick_up_hour = dayjs(pickUpDateTime).format("HH");
  const drop_off_hour = dayjs(dropOffDateTime).format("HH");
  return {
    properties: {
      market: pickUpLocation?.label,
      airport:
        pickUpLocation?.category === AutocompleteCategory.airports ||
        dropOffLocation?.category === AutocompleteCategory.airports,
      pick_up_date,
      pick_up_hour,
      drop_off_date,
      drop_off_hour,
      search_age: driverAge,
      same_pickUp_dropOff: pickUpLocation?.id === dropOffLocation?.id,
      cars_advance,
    },
  };
};

export const getCarEntryTrackingProperties = createSelector(
  CarsAvailabilitySelectors.getSearchFormPickupDateTime,
  CarsAvailabilitySelectors.getSearchFormPickupLocation,
  CarsAvailabilitySelectors.getSearchFormDropoffDateTime,
  CarsAvailabilitySelectors.getSearchFormDropoffLocation,
  CarsAvailabilitySelectors.getSearchFormDriverAge,
  formatCarEntryTrackingProperties
);

const formatCarsEntryEncryptedProperties = (
  pickUpEncryptedProperties,
  dropOffEncryptedProperties
): string[] => {
  return [
    pickUpEncryptedProperties?.encryptedProperties ?? "",
    dropOffEncryptedProperties?.encryptedProperties ?? "",
  ].filter(Boolean);
};

export const getCarEntryEncryptedTrackingProperties = createSelector(
  CarsAvailabilitySelectors.getSearchFormPickUpLocationTrackingProperties,
  CarsAvailabilitySelectors.getSearchFormDropOffLocationTrackingProperties,
  formatCarsEntryEncryptedProperties
);

const formatViewedCarsListTrackingProperties = (
  carEntryProperties: CarsEntryTrackingProperties,
  cars: VehicleAvailability[],
  pickUpLocationCountry: string,
  pickUpLocation: CarLocationSuggestion,
  dropOffLocation: CarLocationSuggestion,
  pickUpLocationTrackingProperties: TrackingProperties,
  dropOffLocationTrackingProperties: TrackingProperties
): ViewedCarsListTrackingProperties => {
  // @ts-expect-error toSorted is widely available at the time of this comment
  const cheapest = cars.toSorted(
    (a, b) => a.totalCharge.fiat.value - b.totalCharge.fiat.value
  )[0];
  return {
    properties: {
      number_of_results: cars?.length,
      cheapest_price: cheapest?.totalCharge.fiat.value ?? 0,
      cheapest_provider:
        cheapest?.supplierName ??
        cheapest?.supplierRef ??
        cheapest?.vendorRef ??
        "",
      pick_up_place_country: pickUpLocationCountry,
      pick_up_place_city: pickUpLocation?.label,
      pick_up_place_id: (pickUpLocation?.id as Grounds)?.Id,
      drop_off_place_city: dropOffLocation?.label,
      drop_off_place_country: "", // TODO BE doesn't return this value like it does pickUpLocationCountry
      drop_off_place_id: (dropOffLocation?.id as Grounds)?.Id,
      ...carEntryProperties?.properties,
      ...pickUpLocationTrackingProperties?.properties,
      ...dropOffLocationTrackingProperties?.properties,
    },
  };
};

export const getViewedCarListTrackingProperties = createSelector(
  getCarEntryTrackingProperties,
  CarsAvailabilitySelectors.getRawCars,
  CarsAvailabilitySelectors.getPickUpLocationCountry,
  CarsAvailabilitySelectors.getSearchFormPickupLocation,
  CarsAvailabilitySelectors.getSearchFormDropoffLocation,
  CarsAvailabilitySelectors.getSearchFormPickUpLocationTrackingProperties,
  CarsAvailabilitySelectors.getSearchFormDropOffLocationTrackingProperties,
  formatViewedCarsListTrackingProperties
);

const formatViewedCarsListEncryptedTrackingProperties = (
  carEntryEncryptedTrackingProperties: string[]
): string[] => {
  return [...carEntryEncryptedTrackingProperties].filter(Boolean);
};

export const getViewedCarListEncryptedTrackingProperties = createSelector(
  getCarEntryEncryptedTrackingProperties,
  formatViewedCarsListEncryptedTrackingProperties
);

const formatAppliedCarsFilterTrackingProperties = (
  carEntryTrackingProperties: CarsEntryTrackingProperties,
  viewedCarsListTrackingProperties: ViewedCarsListTrackingProperties,
  filters: FilterState
): AppliedCarsFilterTrackingProperties => {
  return {
    properties: {
      ...carEntryTrackingProperties?.properties,
      ...viewedCarsListTrackingProperties?.properties,
      applied_filter: Object.keys(filters)
        .filter((filterKey) => !!filters[filterKey])
        .join(","),
    },
  };
};

export const getAppliedCarsFilterTrackingProperties = createSelector(
  getCarEntryTrackingProperties,
  getViewedCarListTrackingProperties,
  CarsAvailabilitySelectors.getFilters,
  formatAppliedCarsFilterTrackingProperties
);

const formatAppliedCarsFilterEncryptedTrackingProperties = (
  carEntryEncryptedTrackingProperties: string[],
  viewedCarListEncryptedTrackingProperties: string[]
): string[] => {
  return [
    ...carEntryEncryptedTrackingProperties,
    ...viewedCarListEncryptedTrackingProperties,
  ].filter(Boolean);
};

export const getAppliedCarsFilterEncryptedTrackingProperties = createSelector(
  getCarEntryEncryptedTrackingProperties,
  getViewedCarListEncryptedTrackingProperties,
  formatAppliedCarsFilterEncryptedTrackingProperties
);

const formatViewedCarDetailsTrackingProperties = (
  carEntryTrackingProperties: CarsEntryTrackingProperties,
  viewedCarsListTrackingProperties: ViewedCarsListTrackingProperties,
  selectedVehicle: VehicleAvailabilityWithSupplier
): ViewedCarDetailsTrackingProperties => {
  return {
    properties: {
      ...carEntryTrackingProperties?.properties,
      ...viewedCarsListTrackingProperties?.properties,
      fuel_policy: selectedVehicle?.fuelPolicy?.desc ?? "",
      vehicle_type: selectedVehicle?.vehicle?.vehicleType ?? "",
      total_price: selectedVehicle?.totalCharge?.fiat?.value,
      free_cancellation: false,
      unlimited_mileage: false,
      automatic:
        selectedVehicle?.vehicle?.transmissionType ===
        TransmissionTypeEnum.Automatic,
      supplier:
        selectedVehicle?.supplierName ?? selectedVehicle?.supplier?.name,
      vendor: selectedVehicle?.vendorRef,
      non_refundable: selectedVehicle?.cancellationPolicy?.RestrictionCancel,
    },
  };
};

export const getViewedCarDetailsTrackingProperties = createSelector(
  getCarEntryTrackingProperties,
  getViewedCarListTrackingProperties,
  CarsShopSelectors.getSelectedVehicle,
  formatViewedCarDetailsTrackingProperties
);

const formatViewedCarDetailsEncryptedTrackingProperties = (
  carEntryEncryptedTrackingProperties: string[],
  viewedCarListEncryptedTrackingProperties: string[]
): string[] => {
  return [
    ...carEntryEncryptedTrackingProperties,
    ...viewedCarListEncryptedTrackingProperties,
  ].filter(Boolean);
};

export const getViewedCarDetailsEncryptedTrackingProperties = createSelector(
  getCarEntryEncryptedTrackingProperties,
  getViewedCarListEncryptedTrackingProperties,
  formatViewedCarDetailsEncryptedTrackingProperties
);
